
import {Exception} from '@/api/interfaces';
import {defineComponent} from "vue";
import {CodeDescriptionRestDto, SimpleDocumentContentRestDto} from "@/api/pharma-cms-content-block/models";
import {StatusEnum} from "@/api/enums/status-enum";
import CbForm from "@/components/layouts/content-blocks/forms/CbForm.vue";
import {MultilingualRestDtoModel} from "@/models/MultilingualRestDtoModel";
import InputImage from "@/components/UI/InputImage.vue";
import {DpModuleEnum} from "@/api/enums/dp-module-enum";
import {SimpleContentBlockRestService} from "@/services/rest/cms-content-block/SimpleContentBlockRestService";
import {
  SimpleDocumentContentRestDtoModel
} from "@/models/api/pharma-cms-simple-content-block/SimpleDocumentContentRestDtoModel";

export default defineComponent({
  name: "CbFormDocument",
  components: {InputImage, CbForm},
  props: {
    cbId: {
      type: String,
      required: false,
    },
    cbType: {
      type: String,
      required: true,
    },
    canCopyFromLibrary: {
      type: Boolean,
      required: false,
      default: false,
    },
    canRestoreDefaultValues: {
      type: Boolean,
      required: false,
      default: false,
    },
    docType: {
      type: String,
      required: true,
    },
    contentLibraryCode: {
      type: String,
      required: false,
    },
    isLibrary: {
      type: Boolean,
      required: false,
      default: false,
    },
    isPublishable: {
      type: Boolean,
      required: false,
      default: false,
    },
    mode: {
      type: String,
      required: true,
    },
    returnRoute: {
      type: Object,
      required: false,
    },
    title: {
      type: String,
      required: true,
    },
    description: {
      type: String,
      required: false,
    },
    description2: {
      type: String,
      required: false,
    },
    description3: {
      type: String,
      required: false,
    },
    description4: {
      type: String,
      required: false,
    },
  },
  data() {
    return {
      error: null,
      exceptions: [] as Exception[],
      isReady: false as boolean,
      documentKey: null as string | null | undefined,
      initialDocumentKey: '' as string | null | undefined,
      documentContent: SimpleDocumentContentRestDtoModel.createWithDefaults() as SimpleDocumentContentRestDto,
      savedSuccessfully: false as boolean,
      statusOptions: [
        {code: StatusEnum.PUBLISHED, description: this.$t('published')},
        {code: StatusEnum.NOT_PUBLISHED, description: this.$t('notPublished')},
      ] as CodeDescriptionRestDto[]
    };
  },
  mounted() {
    this.initialize();
  },
  computed: {
    DpModuleEnum() {
      return DpModuleEnum;
    },
    isContentChanged(): boolean {
      return this.initialDocumentKey !== this.documentKey;
    },
  },
  methods: {
    async restoreInitialValue() {
      this.documentKey = this.initialDocumentKey;
      await this.submitData();
      this.initialDocumentKey = this.documentContent.document_key?.nl;
    },
    async submitData() {
      try {
        if (this.documentKey === null) {
          await SimpleContentBlockRestService.getInstance().deleteSimpleContentBlock(this.cbType);
        } else {
          this.documentContent.document_key = new MultilingualRestDtoModel(null, null, null, this.documentKey ?? null);
          await SimpleContentBlockRestService.getInstance().updateSimpleContentBlockDocument(this.cbType, this.documentContent);
        }

        await this.afterSave();
      } catch (exceptions: any) {
        this.exceptions = exceptions;
      }
    },
    async restoreDefaultValues() {
      this.exceptions = [];

      try {
        this.isReady = false;
        this.documentContent = await SimpleContentBlockRestService.getInstance().getStandardSimpleContentBlockDocument(this.cbType);
        this.documentKey = this.documentContent.document_key?.nl;
      } catch (exceptions: any) {
        this.exceptions = exceptions;
      } finally {
        this.isReady = true;
      }
    },
    async initialize(): Promise<void> {
      await this.reloadContent();
      this.initialDocumentKey = this.documentContent.document_key?.nl;
    },
    async reloadContent(): Promise<void> {
      this.isReady = false;
      this.exceptions = [];

      try {
        this.documentContent = await SimpleContentBlockRestService.getInstance().getSimpleContentBlockDocument(this.cbType);
        this.documentKey = this.documentContent.document_key?.nl;
      } catch (exceptions: any) {
        this.exceptions = exceptions;
      } finally {
        this.isReady = true;
      }
    },
    async afterSave() {
      if (this.returnRoute) {
        await this.$router.push(this.returnRoute);
      } else {
        this.reloadContent().then(() => {
          this.savedSuccessfully = true;
          setTimeout(() => this.savedSuccessfully = false, 2000);
        });
      }
    }
  }
});
